/**
 * Chat store for managing UI state
 * Handles guest sessions, sidebar, and message input
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const GUEST_SESSION_KEY = 'election_guest_session_id';

interface ChatState {
    // UI state
    sidebarOpen: boolean;
    currentConversationId: number | null;
    messageInputValue: string;
    isAiTyping: boolean;
    selectedCandidateId: number | null;

    // Guest session (persisted to localStorage)
    guestSessionId: string | null;
}

interface ChatActions {
    // UI actions
    toggleSidebar: () => void;
    setSidebarOpen: (open: boolean) => void;
    setCurrentConversation: (id: number | null) => void;
    setMessageInput: (value: string) => void;
    clearMessageInput: () => void;
    setAiTyping: (typing: boolean) => void;
    setSelectedCandidate: (id: number | null) => void;

    // Guest session actions
    initGuestSession: () => string;
    clearGuestSession: () => void;
    getGuestSessionId: () => string | null;
}

type ChatStore = ChatState & ChatActions;

// Generate UUID
function generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const useChatStore = create<ChatStore>()(
    persist(
        (set, get) => ({
            // Initial state
            sidebarOpen: false,
            currentConversationId: null,
            messageInputValue: '',
            isAiTyping: false,
            selectedCandidateId: null,
            guestSessionId: null,

            // UI actions
            toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

            setSidebarOpen: (open) => set({ sidebarOpen: open }),

            setCurrentConversation: (id) => set({
                currentConversationId: id,
                messageInputValue: '', // Clear input when switching conversations
            }),

            setMessageInput: (value) => set({ messageInputValue: value }),

            clearMessageInput: () => set({ messageInputValue: '' }),

            setAiTyping: (typing) => set({ isAiTyping: typing }),

            setSelectedCandidate: (id) => set({ selectedCandidateId: id }),

            // Guest session actions
            initGuestSession: () => {
                const existing = get().guestSessionId;
                if (existing) return existing;

                const newId = generateUUID();
                set({ guestSessionId: newId });
                return newId;
            },

            clearGuestSession: () => set({
                guestSessionId: null,
                currentConversationId: null,
            }),

            getGuestSessionId: () => get().guestSessionId,
        }),
        {
            name: GUEST_SESSION_KEY,
            partialize: (state) => ({
                guestSessionId: state.guestSessionId,
                // Don't persist UI state
            }),
        }
    )
);
